// Components/Filters.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { debounce } from 'lodash';
import { flushSync } from 'react-dom';
import {
  Stack,
  TextField,
  Button,
  InputAdornment,
  Autocomplete,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import {
  LocalizationProvider,
  DatePicker,
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';

// Cookie helpers
const setCookie = (name, value, days = 365) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
};
const getCookie = (name) => {
  const m = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
  return m ? decodeURIComponent(m.pop()) : null;
};

export default function Filters({
  filterStart,
  filterEnd,
  setFilterStart,
  setFilterEnd,
  handleApply,
  exportExcel,
  navigate,
  search,
  setSearch,
  departments,
  selectedDepartment,
  setSelectedDepartment,
  teams,
  selectedTeam,
  setSelectedTeam,
  // опционально: если родитель хочет получать флаг
  onToggleShowInactive, // (bool) => void
}) {
  const [localStart, setLocalStart] = useState(filterStart);
  const [localEnd, setLocalEnd] = useState(filterEnd);
  const [localSearch, setLocalSearch] = useState(search);

  // Состояние галочки (запоминается в cookie)
  const [showInactive, setShowInactive] = useState(false);
  useEffect(() => {
    const v = getCookie('show_inactive_users');
    const init = v === '1';
    setShowInactive(init);
    if (typeof onToggleShowInactive === 'function') onToggleShowInactive(init);
  }, [onToggleShowInactive]);

  const handleShowInactive = (e) => {
    const v = e.target.checked;
    setShowInactive(v);
    setCookie('show_inactive_users', v ? '1' : '0');
    if (typeof onToggleShowInactive === 'function') onToggleShowInactive(v);
  };

  // Filter teams based on selected department
  const filteredTeams = selectedDepartment?.id
    ? teams.filter((team) => Number(team.department_id) === Number(selectedDepartment.id))
    : teams;

  useEffect(() => setLocalStart(filterStart), [filterStart]);
  useEffect(() => setLocalEnd(filterEnd), [filterEnd]);
  useEffect(() => setLocalSearch(search), [search]);

  // Reset selected team when department changes if the team is not in the new department
  useEffect(() => {
    console.log('Teams passed to Filters:', teams);
    console.log('Filtered teams:', filteredTeams);
    if (selectedTeam?.id && selectedDepartment?.id) {
      const isTeamInDepartment = teams.some(
        (team) => team.id === selectedTeam.id && Number(team.department_id) === Number(selectedDepartment.id)
      );
      if (!isTeamInDepartment) {
        setSelectedTeam(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDepartment, selectedTeam, teams, filteredTeams]);

  const debouncedSetSearch = useCallback(
    debounce((value) => setSearch(value), 300),
    [setSearch]
  );

  const onApply = () => {
    flushSync(() => {
      if (localStart) setFilterStart(localStart);
      if (localEnd) setFilterEnd(localEnd);
    });
    handleApply(localStart, localEnd);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', gap: 2 }}>
        <TextField
          size="small"
          placeholder="Search employees"
          value={localSearch}
          onChange={(e) => {
            setLocalSearch(e.target.value);
            debouncedSetSearch(e.target.value);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: 200 }}
        />

        <Autocomplete
          options={[{ id: null, name: 'All Departments' }, ...departments]}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === (value?.id || null)}
          value={selectedDepartment}
          onChange={(e, value) => setSelectedDepartment(value)}
          renderInput={(params) => <TextField {...params} label="Department" size="small" />}
          sx={{ width: 200 }}
        />

        <Autocomplete
          options={[{ id: null, name: 'All Teams' }, ...filteredTeams]}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === (value?.id || null)}
          value={selectedTeam}
          onChange={(e, value) => setSelectedTeam(value)}
          renderInput={(params) => <TextField {...params} label="Team" size="small" />}
          sx={{ width: 200 }}
        />

        <DatePicker
          label="From"
          format="dd/MM/yyyy"
          value={localStart}
          onChange={setLocalStart}
          slotProps={{ textField: { size: 'small' } }}
        />
        <DatePicker
          label="To"
          format="dd/MM/yyyy"
          value={localEnd}
          onChange={setLocalEnd}
          slotProps={{ textField: { size: 'small' } }}
        />

        <FormControlLabel
          control={<Checkbox checked={showInactive} onChange={handleShowInactive} />}
          label="Show deactivated users"
        />

        <Button variant="contained" onClick={onApply}>
          Apply
        </Button>
        <Button startIcon={<DownloadIcon />} onClick={exportExcel}>
          Export
        </Button>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Back
        </Button>
      </Stack>
    </LocalizationProvider>
  );
}
